<template>
	<AppPageHeader v-if="flowData">
		<template #left-side>
			<div>
				<div class="display-flex flex-row flex-align-center">
					<AnemicButton @click="$router.back()" class="back-button mr-2" width="25" height="25" fab x-small>
						<AppIcon name="back" />
					</AnemicButton>
					<FeatureIconsCard :flowData="flowData" />
					<div class="display-flex flex-column ml-4 flex-justify-center">
						<div class="flow-title">{{ flowData.workflow_title }}</div>
						<FlowFullDescription :flowData="flowData" />
					</div>
				</div>
			</div>
		</template>

		<template #right-side>
			<div>
				<div class="left-side display-flex flex-row flex-align-center">
					<div v-if="showEditSetup" @click="editConfiguration" class="display-flex flex-row flex-align-center mr-5 setup-text">
						<SIcon class="mr-1" name="settings" width="16" style="margin-top: 3px" />
						Setup
					</div>
					<ShareButton v-if="canShare" :workflowId="flowData.id" class="" dense>Share</ShareButton>
					<router-link
						v-if="editFlowEnabled || hasProjectViewPermissions"
						:to="moduleEditRouterTo"
						class="text-decoration-none font-weight-regular"
					>
						<OutlinedButton class="ml-4">
							{{ openFlowText }}
						</OutlinedButton>
					</router-link>
					<ModuleActivenessStatus
						class="ml-4"
						width="100px"
						height="33px"
						:flowData="flowData"
						@open-setup="editConfiguration"
						:hasToggleActivePermissions="hasToggleActivePermissions"
					/>
				</div>
			</div>
		</template>
	</AppPageHeader>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AppPageHeader from '@/views/AppLayout/AppPageHeader.vue';
import {
	hasWorkflowEditGeneralConfigurationPermissions,
	hasWorkflowEditPermissions,
	getWorkflow,
	hasWorkflowToggleActivePermissions,
} from '@/store/modules/workflows/getters';
import ShareButton from '@/components/Project/share/ShareButton';
import { getWorkflowData } from '@/store/modules/workflows/actions';
import FlowFullDescription from '@/views/Flow/FlowFullDescription.vue';
import { projectHasPermission } from '@/store/modules/projects/getters';
import FeatureIconsCard from '@/components/Modules/FeatureIconsCard.vue';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { selectWorkflowId } from '@/store/modules/selectedWorkflow/actions';
import ModuleActivenessStatus from '@/components/Modules/ModuleActivenessStatus.vue';
import WorkflowConfigurationResponseService from '@/utils/workflow-configuration-response-service';
import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';

export default {
	name: 'FlowDetailsHeader',
	props: { projectIdToReturn: { type: [Number, String], default: null } },
	components: {
		AppPageHeader,
		FeatureIconsCard,
		FlowFullDescription,
		ModuleActivenessStatus,
		ShareButton,
	},
	computed: {
		...mapGetters([
			getWorkflow.fullPath,
			hasWorkflowEditPermissions.fullPath,
			isConfigurationsOn.fullPath,
			projectHasPermission.fullPath,
			hasWorkflowEditGeneralConfigurationPermissions.fullPath,
			hasWorkflowToggleActivePermissions.fullPath,
		]),
		flowData() {
			return this[getWorkflow.fullPath](this.wfID);
		},
		canShare() {
			return !!this.flowData?.current_version && this.flowData?.is_published && this.hasSharePermissions;
		},
		installedFromInstallable() {
			return !!this.flowData.installed_from;
		},
		hasSharePermissions() {
			return this.hasEditPermissions || this[projectHasPermission.fullPath](this.flowData.project, ProjectPermissions.ChangeWorkflowUsers);
		},
		hasProjectViewPermissions() {
			return this[projectHasPermission.fullPath](this.flowData.project, ProjectPermissions.ViewProject);
		},
		openFlowText() {
			return this.editFlowEnabled ? 'Edit' : this.hasProjectViewPermissions ? 'View' : '';
		},
		hasEditPermissions() {
			return this[hasWorkflowEditPermissions.fullPath](this.flowData.id);
		},
		hasToggleActivePermissions() {
			return this[hasWorkflowToggleActivePermissions.fullPath](this.flowData.id);
		},
		editFlowEnabled() {
			return this.hasEditPermissions && !this.installedFromInstallable;
		},
		isOwner() {
			return this.flowData.is_owner;
		},
		moduleEditRouterTo() {
			return { name: 'Feature', params: { pID: this.flowData.project, wfID: this.flowData.id, projectIdToReturn: this.projectIdToReturn } };
		},
		userSetupId() {
			return this.flowData.current_version_user_setup;
		},
		generalSetupId() {
			return this.flowData.current_version_general_setup;
		},
		showEditSetup() {
			return this[isConfigurationsOn.fullPath] && (this.generalSetupId || this.userSetupId) && this.hasEditPermissions;
		},
		hasEditGeneralConfigurationPermissions() {
			return this[hasWorkflowEditGeneralConfigurationPermissions.fullPath](this.flowData.id);
		},
	},
	methods: {
		...mapActions([getWorkflowData.fullPath, selectWorkflowId.fullPath]),
		editConfiguration() {
			this[selectWorkflowId.fullPath]({ id: this.flowData.id });
			return WorkflowConfigurationResponseService.editAndSaveConfiguration(
				{
					generalConfigurationId: this.hasEditGeneralConfigurationPermissions ? this.generalSetupId : null,
					userSpecificConfigurationId: this.userSetupId,
				},
				false
			);
		},
	},
	data: () => ({
		wfID: null,
	}),
	created() {
		this.wfID = this.$route.params.wfID;
		this[getWorkflowData.fullPath]({ id: this.wfID });
	},
};
</script>

<style scoped lang="scss">
.flow-title {
	font-weight: 600;
	font-size: 18px;
	color: #4d4c52;
	line-height: 20px;
}
.flow-description {
	font-weight: 400;
	font-size: 12px;
	color: #9e9e9e;
	line-height: 18px;
}

.left-side ::v-deep(.v-btn) {
	width: 100px !important;
	height: 33px !important;
	font-weight: 400 !important;
	font-size: 16px !important;
	color: var(--share-button-text) !important;
	border: 1px solid #4d4c52 !important;
	border-radius: 6px !important;
}
.setup-text {
	cursor: pointer;
	font-size: 14px;
	font-weight: 600;
	color: #4d4c52;
}
.tab-button {
	padding: 2px !important;
	font-size: 14px !important;
	font-weight: 600 !important;
	color: #9e9e9e !important;
	line-height: 20px !important;
	border-radius: 1px !important;
	background: transparent !important;
	&.active {
		color: #4d4c52 !important;
		border-bottom: 2px solid #4d4c52 !important;
	}
}
.router-link {
	color: #4d4c52 !important;
}
</style>
